import api from "./api";

export interface DocumentTemplateItem {
  template_id: string;
  source: "file" | "local";
  name: string;
  file_type: string;
  updated_at?: string | null;
  file_id?: number | null;
}

export interface TemplateListResponse {
  templates: DocumentTemplateItem[];
}

export interface TemplatePlaceholderResponse {
  placeholders: string[];
}

export interface TemplateGenerateResponse {
  file_id: number;
  file_name: string;
  download_url: string;
  preview?: {
    mode: "docx" | "pptx";
    slides?: Array<Record<string, unknown>>;
    fields?: Record<string, string>;
    table_rows?: Array<Record<string, unknown>>;
  };
}

export interface AIGenerateRequest {
  mode: "docx" | "pptx";
  template_id?: string;
  topic: string;
  requirements?: string;
  file_ids?: number[];
  output_filename?: string;
}

export interface DocumentGenerationState {
  status: "idle" | "running" | "success" | "error";
  request: AIGenerateRequest | null;
  result: TemplateGenerateResponse | null;
  error: string | null;
}

const DOC_GEN_STATE_KEY = "doc-gen:state";
const DOC_GEN_EVENT = "doc-gen-state-changed";

let generationState: DocumentGenerationState = {
  status: "idle",
  request: null,
  result: null,
  error: null,
};
let activeGenerationPromise: Promise<TemplateGenerateResponse> | null = null;

function persistGenerationState() {
  sessionStorage.setItem(DOC_GEN_STATE_KEY, JSON.stringify(generationState));
  window.dispatchEvent(new CustomEvent(DOC_GEN_EVENT, { detail: generationState }));
}

function safeHydrateGenerationState() {
  try {
    const raw = sessionStorage.getItem(DOC_GEN_STATE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as DocumentGenerationState;
    if (parsed && parsed.status) generationState = parsed;
  } catch {
    // ignore broken cached state
  }
}

safeHydrateGenerationState();

export function getGenerationState(): DocumentGenerationState {
  return generationState;
}

export function clearGenerationState() {
  generationState = { status: "idle", request: null, result: null, error: null };
  sessionStorage.removeItem(DOC_GEN_STATE_KEY);
  window.dispatchEvent(new CustomEvent(DOC_GEN_EVENT, { detail: generationState }));
}

export function subscribeGenerationState(listener: (state: DocumentGenerationState) => void) {
  const handler = (event: Event) => {
    listener((event as CustomEvent<DocumentGenerationState>).detail ?? generationState);
  };
  window.addEventListener(DOC_GEN_EVENT, handler);
  return () => window.removeEventListener(DOC_GEN_EVENT, handler);
}

export async function listTemplates(): Promise<DocumentTemplateItem[]> {
  const res = await api.get<TemplateListResponse>("/document/templates");
  return res.templates || [];
}

export async function uploadTemplate(file: File): Promise<DocumentTemplateItem> {
  const form = new FormData();
  form.append("file", file);
  return api.upload<DocumentTemplateItem>("/document/templates/upload", form);
}

export async function getTemplatePlaceholders(templateId: string): Promise<string[]> {
  const res = await api.get<TemplatePlaceholderResponse>(
    `/document/templates/placeholders?template_id=${encodeURIComponent(templateId)}`
  );
  return res.placeholders || [];
}

export async function generateWithTemplate(
  templateId: string,
  data: Record<string, string>,
  outputFilename?: string
): Promise<TemplateGenerateResponse> {
  return api.post<TemplateGenerateResponse>("/document/templates/generate", {
    template_id: templateId,
    data,
    output_filename: outputFilename,
  });
}

export async function generateWithAI(request: AIGenerateRequest): Promise<TemplateGenerateResponse> {
  generationState = {
    status: "running",
    request,
    result: null,
    error: null,
  };
  persistGenerationState();
  activeGenerationPromise = api
    .post<TemplateGenerateResponse>("/document/ai-generate", request)
    .then((result) => {
      generationState = {
        status: "success",
        request,
        result,
        error: null,
      };
      persistGenerationState();
      return result;
    })
    .catch((error) => {
      generationState = {
        status: "error",
        request,
        result: null,
        error: error instanceof Error ? error.message : "生成失败",
      };
      persistGenerationState();
      throw error;
    })
    .finally(() => {
      activeGenerationPromise = null;
    });

  return activeGenerationPromise;
}

export function waitForGeneration(): Promise<TemplateGenerateResponse | null> {
  if (activeGenerationPromise) {
    return activeGenerationPromise;
  }
  return Promise.resolve(generationState.result);
}
